import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Tabs, Select, Button, Image, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import s from './s.module.less';
import ProviderStore from 'store/Provider';
import { Provider } from 'types/provider';
import { getProviderNameDisplay } from 'utils/provider';
import PrivateFileUpload from 'components/form/FileUploadPrivate';
import { useFileUpload } from 'hooks/useFileUpload';
import { TFile } from 'types/inbox';
import { getProviderPrivateFileSigned, uploadProviderPrivateFile, createProviderSignature } from 'api/common';
import { FileUploadResult } from 'hooks/useFileUpload';
import { ESignatureType, IProviderSignature } from 'types/treatment';
interface IFileUploadResult extends TFile {
    id: number;
}

interface SignatureModalProps {
    open: boolean;
    onCancel: () => void;
    onConfirm?: (signature: IProviderSignature) => void;
    initialData?: IProviderSignature;
    // 以下三项供 credentialing agreement 页 group owner 替成员加签名用；不传时行为与 EHR 现状一致
    // 签名存在谁名下（切 header 到该 provider）
    providerId?: number | string;
    // 默认的全名（不传用登录者本人的名字）
    defaultFullName?: string;
    // 弹窗底部的授权文案（替别人签时换成「已获得本人许可」）
    consentText?: React.ReactNode;
}

const FONTS = [
    { label: 'Allura', value: 'Allura', className: s.fontAllura },
    { label: 'Caveat', value: 'Caveat', className: s.fontCaveat },
    { label: 'Dancing Script', value: 'DancingScript', className: s.fontDancingScript },
    { label: 'Great Vibes', value: 'GreatVibes', className: s.fontGreatVibes },
    { label: 'Parisienne', value: 'Parisienne', className: s.fontParisienne },
    { label: 'Satisfy', value: 'Satisfy', className: s.fontSatisfy },
    { label: 'Yellowtail', value: 'Yellowtail', className: s.fontYellowtail },
];

const base64ToFile = async (base64: string, filename: string) =>
    new File([await (await fetch(base64)).blob()], filename, { type: 'image/png' });

const SignatureModal: React.FC<SignatureModalProps> = ({
    open,
    onCancel,
    onConfirm,
    initialData,
    providerId,
    defaultFullName,
    consentText,
}) => {
    const [fullName, setFullName] = useState('');
    const [initials, setInitials] = useState('');
    const [selectedFont, setSelectedFont] = useState(FONTS[0].value);
    const [activeTab, setActiveTab] = useState<ESignatureType>(initialData?.signatureType || ESignatureType.STYLE);
    const [drawnSignature, setDrawnSignature] = useState<string | null>(null);
    const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
    // signatureImage stores the fileKey for upload type
    const [signatureImage, setSignatureImage] = useState<string | undefined>(undefined);
    // signaturePreviewUrl stores the displayable URL fetched from fileKey for all type
    const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | undefined>(undefined);
    const [isDrawModified, setIsDrawModified] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const signaturePadRef = useRef<SignatureCanvas>(null);
    const previewBoxRef = useRef<HTMLDivElement>(null);

    const [getProvider] = ProviderStore.useStore();
    const provider: Provider = getProvider('data') as Provider;
    const defaultName = defaultFullName ?? getProviderNameDisplay(provider);
    const defaultInitials = defaultFullName
        ? defaultFullName
              .split(/\s+/)
              .filter(Boolean)
              .map((w) => w.charAt(0).toUpperCase())
              .join('')
        : `${provider?.firstName?.charAt(0)?.toUpperCase() || ''}${provider?.lastName?.charAt(0)?.toUpperCase() || ''}`;

    const { loading, uploadFile } = useFileUpload({
        accept: 'image/*',
        maxSize: 10,
        formData: {
            uploadTypeEnum: 'PROVIDER_PRIVATE',
        },
        uploadFn: uploadProviderPrivateFile,
        formatFileData: async (result: IFileUploadResult) => {
            const data: FileUploadResult = {
                id: result.id,
                url: result.fileUri,
                name: result.fileOriginName,
                type: result.fileExtension,
            };

            const res = await getProviderPrivateFileSigned(result.fileUri);
            if (!res.error) {
                data.previewUrl = res.data?.data?.singedUrl;
            }
            return data;
        },
    });

    const fetchPreviewUrl = async (fileKey?: string): Promise<string | undefined> => {
        if (fileKey) {
            try {
                const res = await getProviderPrivateFileSigned(fileKey);
                if (!res.error && res.data?.data?.singedUrl) {
                    return res.data.data.singedUrl;
                }
            } catch (e) {
                console.error(e);
            }
        }
        return undefined;
    };

    // Initialize state when modal opens
    useEffect(() => {
        if (!open) return;

        setFullName(initialData?.fullName || defaultName || '');
        setInitials(initialData?.initials || defaultInitials || '');
        setActiveTab(initialData?.signatureType || ESignatureType.STYLE);
        setIsDrawModified(false);
        setSignaturePreviewUrl(undefined);
        setDrawnSignature(null);

        if (initialData?.fontName) {
            const fontExists = FONTS.find((f) => f.value === initialData.fontName);
            if (fontExists) {
                setSelectedFont(initialData.fontName);
            }
        }

        if (initialData?.signatureFileKey) {
            // Only set signatureImage for UPLOAD type; DRAW/STYLE use their own display logic
            setSignatureImage(
                initialData.signatureType === ESignatureType.UPLOAD ? initialData.signatureFileKey : undefined,
            );

            const initPreview = async () => {
                const url = await fetchPreviewUrl(initialData.signatureFileKey);
                if (url) {
                    setSignaturePreviewUrl(url);
                    if (initialData.signatureType === ESignatureType.DRAW) {
                        setHasDrawnSignature(true);
                    }
                }
            };
            initPreview();
        } else {
            setSignatureImage(undefined);
            setHasDrawnSignature(false);
        }
    }, [open, initialData, defaultName, defaultInitials]);

    // Load drawn signature into canvas when draw tab is active
    useEffect(() => {
        if (activeTab === ESignatureType.DRAW && signaturePadRef.current) {
            if (drawnSignature) {
                signaturePadRef.current.fromDataURL(drawnSignature);
                setHasDrawnSignature(true);
            } else {
                signaturePadRef.current.clear();
                // Don't reset hasDrawnSignature if we have existing preview (edit mode, not yet modified)
                if (!signaturePreviewUrl || isDrawModified) {
                    setHasDrawnSignature(false);
                }
            }
        }
    }, [activeTab, drawnSignature]);

    const getCanvasImage = (): string | null => {
        if (activeTab === ESignatureType.DRAW && signaturePadRef.current) {
            if (signaturePadRef.current.isEmpty()) {
                return null;
            }
            return signaturePadRef.current.toDataURL('image/png');
        }
        return null;
    };

    const getPreviewBoxImage = async (): Promise<string | null> => {
        if (activeTab === ESignatureType.STYLE && previewBoxRef.current) {
            try {
                const element = previewBoxRef.current;
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    return null;
                }

                const scale = 2;
                canvas.width = element.offsetWidth * scale;
                canvas.height = element.offsetHeight * scale;
                ctx.scale(scale, scale);

                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, element.offsetWidth, element.offsetHeight);

                ctx.fillStyle = '#111827';
                ctx.font = `50px "${selectedFont}", cursive`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';

                const padding = 24;
                const gap = 40;
                let currentX = padding;
                const centerY = element.offsetHeight / 2;

                ctx.fillText(fullName, currentX, centerY);
                if (initials) {
                    const fullNameWidth = ctx.measureText(fullName).width;
                    currentX += fullNameWidth + gap;
                    ctx.fillText(initials, currentX, centerY);
                }

                return canvas.toDataURL('image/png');
            } catch (error) {
                console.error('Failed to convert preview box to image:', error);
                return null;
            }
        }
        return null;
    };

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            let finalFileKey: string | undefined;

            if (activeTab === ESignatureType.STYLE) {
                // Style always needs to regenerate image (font/name might have changed)
                const imageData = await getPreviewBoxImage();
                if (imageData) {
                    const file = await base64ToFile(imageData, 'signature.png');
                    const result = await uploadFile(file);
                    if (result) {
                        finalFileKey = result.url;
                    }
                }
            } else if (activeTab === ESignatureType.DRAW) {
                if (isDrawModified) {
                    // User drew a new signature, upload it
                    const canvasImage = getCanvasImage();
                    if (canvasImage) {
                        const file = await base64ToFile(canvasImage, 'signature.png');
                        const result = await uploadFile(file);
                        if (result) {
                            finalFileKey = result.url;
                        }
                    }
                } else {
                    // Use existing fileKey (edit mode, not modified)
                    finalFileKey = initialData?.signatureFileKey;
                }
            } else if (activeTab === ESignatureType.UPLOAD) {
                finalFileKey = signatureImage;
            }

            const signatureData: IProviderSignature = {
                fullName,
                initials,
                isDefault: 1,
                signatureType: activeTab,
                fontName: activeTab === ESignatureType.STYLE ? selectedFont : undefined,
                signatureFileKey: finalFileKey,
            };

            const res = await createProviderSignature(signatureData, providerId);
            if (!res.error) {
                message.success('Signature saved successfully');
                onConfirm?.(signatureData);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignatureEnd = () => {
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
            const imageData = signaturePadRef.current.toDataURL('image/png');
            setDrawnSignature(imageData);
            setHasDrawnSignature(true);
            setIsDrawModified(true);
        } else {
            setHasDrawnSignature(false);
        }
    };

    const handleClearCanvas = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setDrawnSignature(null);
            setHasDrawnSignature(false);
            setIsDrawModified(true);
        }
    };

    const currentFontClass = FONTS.find((f) => f.value === selectedFont)?.className || s.fontAllura;

    const renderSelectStyle = () => {
        const selectOptions = FONTS.map((font) => ({
            label: (
                <span className={font.className} style={{ fontSize: '14px' }}>
                    {fullName.trim() ? fullName : font.label}
                </span>
            ),
            value: font.value,
        }));

        return (
            <div className={s.section}>
                <div className={s.sectionHeader}>
                    <p className={s.sectionTitle}>Preview</p>
                    <Select
                        value={selectedFont}
                        onChange={setSelectedFont}
                        options={selectOptions}
                        style={{ width: 150 }}
                    />
                </div>
                <div className={s.previewBox} ref={previewBoxRef}>
                    {fullName && <div className={`${s.signatureText} ${currentFontClass}`}>{fullName}</div>}
                    {initials && <div className={`${s.signatureText} ${currentFontClass}`}>{initials}</div>}
                </div>
            </div>
        );
    };

    const renderDraw = () => {
        // In edit mode: show existing signature image if not yet modified
        const showExistingImage =
            !isDrawModified && signaturePreviewUrl && initialData?.signatureType === ESignatureType.DRAW;

        return (
            <div className={s.section}>
                <div className={s.sectionHeader}>
                    <p className={s.sectionTitle}>Draw your signature</p>
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={handleClearCanvas}
                        className={s.clearButton}
                    />
                </div>
                {showExistingImage ? (
                    <div
                        className={s.canvasWrapper}
                        onClick={() => {
                            setIsDrawModified(true);
                            setHasDrawnSignature(false);
                        }}
                        title="Click to redraw"
                    >
                        <img
                            src={signaturePreviewUrl}
                            alt="Existing signature"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                margin: 'auto',
                            }}
                        />
                    </div>
                ) : (
                    <div className={s.canvasWrapper}>
                        <SignatureCanvas
                            ref={signaturePadRef}
                            canvasProps={{
                                className: s.signatureCanvas,
                            }}
                            penColor="#111827"
                            backgroundColor="#FFFFFF"
                            onEnd={handleSignatureEnd}
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderUpload = () => {
        // In edit mode for UPLOAD type, pass the preview URL for display
        const uploadPreviewUrl = initialData?.signatureType === ESignatureType.UPLOAD ? signaturePreviewUrl : undefined;

        return (
            <div className={s.section}>
                <PrivateFileUpload
                    value={signatureImage}
                    onChange={setSignatureImage}
                    uploadType="IMAGE"
                    accept="image/*"
                    notNeedSign
                    previewUrl={uploadPreviewUrl}
                />
            </div>
        );
    };

    return (
        <Modal
            open={open}
            title={initialData?.signatureFileKey ? 'Edit signature' : 'Add signature'}
            onCancel={onCancel}
            width={600}
            className={s.modal}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirm}
                    loading={submitting || loading}
                    disabled={
                        !fullName ||
                        (activeTab === ESignatureType.DRAW && !hasDrawnSignature) ||
                        (activeTab === ESignatureType.UPLOAD && !signatureImage)
                    }
                >
                    Confirm
                </Button>,
            ]}
        >
            <div className={s.content}>
                <p className={s.description}>Confirm your full name, initials, and signature.</p>
                <div className={s.inputsRow}>
                    <div className={s.inputGroup}>
                        <label>
                            Full name <span className={s.required}>*</span>
                        </label>
                        <Input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Enter full name"
                        />
                    </div>
                    <div className={s.inputGroup}>
                        <label>Initials</label>
                        <Input
                            value={initials}
                            onChange={(e) => setInitials(e.target.value)}
                            placeholder="Enter initials"
                        />
                    </div>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as ESignatureType)}
                    destroyInactiveTabPane
                    items={[
                        {
                            key: ESignatureType.STYLE,
                            label: 'Select style',
                            children: renderSelectStyle(),
                        },
                        {
                            key: ESignatureType.DRAW,
                            label: 'Draw',
                            children: renderDraw(),
                        },
                        {
                            key: ESignatureType.UPLOAD,
                            label: 'Upload',
                            children: renderUpload(),
                        },
                    ]}
                />

                <p className={s.agreementText}>
                    {consentText ?? (
                        <>
                            By selecting Confirm, I agree that this mark will be the electronic representation of my
                            signature or initials whenever I use it.
                        </>
                    )}
                </p>
            </div>
        </Modal>
    );
};

export default SignatureModal;
